import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import { Printer, Lock, CheckCircle, XCircle, FileText, Presentation, DownloadSimple } from "@phosphor-icons/react";
import { useFetch } from "@/lib/useFetch";
import { api, errMsg, fmtDate, fmtDateTime } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageTitle, Empty } from "@/components/layout/DashShell";
import { Loader } from "@/components/Blocks";
import { AssetIcon } from "@/components/AssetIcon";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { IndicatorCard } from "@/components/IndicatorCard";

const LEVEL = { Bronze: "#B4793D", Silver: "#94A3B8", Gold: "#E0A21B" };

export function Quizzes() {
  const { data, loading } = useFetch("/me/quizzes");
  if (loading) return <Loader />;
  return (
    <>
      <PageTitle title="Quizzes" sub="Pass with 70% or more to earn the badge. Retake as often as you like." />
      <div className="grid gap-4 md:grid-cols-2">
        {data.map((q) => (
          <div key={q.id} className="panel flex flex-col p-5" data-testid={`quiz-card-${q.slug}`}>
            <div className="flex items-center justify-between">
              <span className="rounded-full px-2.5 py-0.5 text-xs font-bold uppercase text-white" style={{ background: LEVEL[q.level] }}>{q.level}</span>
              {q.passed && <span className="flex items-center gap-1 text-xs font-bold uppercase text-[#1E8E5A]"><CheckCircle weight="fill" /> Passed</span>}
            </div>
            <h3 className="mt-3 font-display text-2xl font-bold uppercase">{q.title}</h3>
            <p className="text-sm text-[#475569]">{q.count} questions · Best: <span className="font-mono-num">{q.best ?? "–"}{q.best != null && "%"}</span> · Attempts: {q.attempts}</p>
            <Link to={`/app/quizzes/${q.slug}`} className="btn-navy mt-4 self-start" data-testid={`quiz-start-${q.slug}`}>{q.attempts ? "Retake" : "Start"}</Link>
          </div>
        ))}
      </div>
    </>
  );
}

export function QuizTake() {
  const { slug } = useParams();
  const { data: qz, loading } = useFetch(`/me/quizzes/${slug}`);
  const [ans, setAns] = useState({});
  const [res, setRes] = useState(null);
  if (loading) return <Loader />;
  const submit = async () => {
    try {
      const { data } = await api.post(`/me/quizzes/${slug}/attempt`, { answers: qz.questions.map((_, i) => ans[i] ?? -1) });
      setRes(data);
      window.scrollTo(0, 0);
      if (data.certificate) toast.success("Certificate unlocked!");
    } catch (e) { toast.error(errMsg(e)); }
  };
  if (res) return (
    <>
      <PageTitle title={qz.title} />
      <div className={`panel mb-4 border-l-4 p-6 ${res.passed ? "border-l-[#1E8E5A]" : "border-l-[#C8382E]"}`} data-testid="quiz-result">
        <div className="font-mono-num text-5xl font-semibold">{res.score}%</div>
        <p className="mt-1 font-semibold">{res.passed ? "Passed. Badge earned." : `Not yet. You need ${res.pass_pct}% to pass.`}</p>
        <div className="mt-4 flex gap-2"><button className="btn-outline" onClick={() => { setRes(null); setAns({}); }} data-testid="quiz-retake-btn">Retake</button><Link to="/app/quizzes" className="btn-navy">All quizzes</Link>{res.certificate && <Link to="/app/certificates" className="btn-gold" data-testid="quiz-cert-link">View certificate</Link>}</div>
      </div>
      <div className="space-y-3">
        {res.review.map((r, i) => (
          <div key={i} className="panel p-5">
            <div className="flex gap-2 font-semibold">{r.correct ? <CheckCircle weight="fill" className="mt-0.5 shrink-0 text-[#1E8E5A]" size={20} /> : <XCircle weight="fill" className="mt-0.5 shrink-0 text-[#C8382E]" size={20} />}{r.q}</div>
            <p className="mt-2 text-sm">Correct answer: <strong>{r.options[r.answer]}</strong>{!r.correct && r.chosen >= 0 && <> · You chose: {r.options[r.chosen]}</>}</p>
            <p className="mt-1 text-sm text-[#475569]">{r.explain}</p>
          </div>
        ))}
      </div>
    </>
  );
  return (
    <>
      <PageTitle title={qz.title} sub={`${qz.questions.length} questions · pass mark ${qz.pass_pct}%`} />
      <div className="space-y-4">
        {qz.questions.map((q, i) => (
          <div key={i} className="panel p-5" data-testid={`quiz-question-${i}`}>
            <div className="font-mono-num text-xs text-[#475569]">Q{i + 1}</div>
            <div className="mt-1 font-semibold">{q.q}</div>
            <div className="mt-3 grid gap-2 sm:grid-cols-2">
              {q.options.map((o, k) => (
                <button key={k} onClick={() => setAns({ ...ans, [i]: k })} data-testid={`quiz-q${i}-opt${k}`}
                  className={`rounded-md border-2 px-3 py-2.5 text-left text-sm transition-colors ${ans[i] === k ? "border-[#E0A21B] bg-[#E0A21B]/10" : "border-[#0B1B33]/10 hover:border-[#0B1B33]/30"}`}>{o}</button>
              ))}
            </div>
          </div>
        ))}
      </div>
      <button className="btn-gold mt-6" disabled={Object.keys(ans).length < qz.questions.length} onClick={submit} data-testid="quiz-submit-btn">Submit answers</button>
    </>
  );
}

export function Badges() {
  const { data, loading } = useFetch("/me/badges");
  if (loading) return <Loader />;
  return (
    <>
      <PageTitle title="Badges" sub={`${data.filter((b) => b.earned_at).length} of ${data.length} earned`} />
      <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
        {data.map((b) => (
          <div key={b.slug} className={`panel flex flex-col items-center p-5 text-center ${b.earned_at ? "" : "opacity-60"}`} data-testid={`badge-${b.slug}`}>
            <div className="relative flex h-20 w-20 items-center justify-center rounded-full border-4" style={{ borderColor: b.earned_at ? LEVEL[b.level] : "#CBD5E1", background: b.earned_at ? "#0B1B33" : "#F4F6FA" }}>
              {b.earned_at ? <AssetIcon name={b.asset} size={34} className="text-[#E0A21B]" /> : <Lock size={28} className="text-[#94A3B8]" />}
            </div>
            <div className="mt-3 font-display text-lg font-bold uppercase leading-tight">{b.name}</div>
            <div className="text-xs font-bold uppercase" style={{ color: LEVEL[b.level] }}>{b.level}</div>
            <p className="mt-1 text-xs text-[#475569]">{b.earned_at ? `Earned ${fmtDate(b.earned_at)}` : b.criteria}</p>
          </div>
        ))}
      </div>
    </>
  );
}

export const CertificateDoc = ({ c }) => (
  <div className="print-area relative mx-auto aspect-[11/8.5] w-full max-w-4xl overflow-hidden rounded-lg border-[10px] border-[#0B1B33] bg-white p-[5%]" data-testid={`certificate-${c.code}`} style={{ breakAfter: "page" }}>
    <div className="absolute inset-3 border-2 border-[#E0A21B]" />
    <div className="relative flex h-full flex-col items-center justify-between text-center">
      <div>
        <div className="font-display text-xl font-extrabold uppercase tracking-[0.3em] text-[#0B1B33]">Mission Control <span className="text-[#E0A21B]">Trading</span></div>
        <div className="mt-[3%] font-display text-[clamp(1.5rem,4vw,3rem)] font-black uppercase text-[#0B1B33]">Certificate of Completion</div>
        <div className="mt-1 text-xs uppercase tracking-widest text-[#475569]">This certifies that</div>
        <div className="mt-[2%] font-display text-[clamp(1.75rem,5vw,3.5rem)] font-bold italic text-[#0B1B33]" data-testid="certificate-student-name">{c.student_name}</div>
        <div className="mt-1 text-sm text-[#475569]">has completed <strong className="text-[#0B1B33]">{c.program_name}</strong> and earned the <strong className="text-[#0B1B33]">{c.badge}</strong> badge.</div>
      </div>
      <div className="flex w-full items-end justify-between gap-4">
        <div className="text-left text-xs"><div className="w-36 border-b border-[#0B1B33] pb-1 font-display text-lg italic">{c.instructor}</div><div className="mt-1 text-[#475569]">Instructor · {fmtDate(c.issued_at)}</div></div>
        <img src="/brand/10_Certificate_Seal_2in.png" alt="Certificate seal" className="h-24 w-24 sm:h-28 sm:w-28" />
        <div className="flex flex-col items-center text-xs"><QRCodeSVG value={`${window.location.origin}/verify/${c.code}`} size={64} fgColor="#0B1B33" /><span className="mt-1 font-mono-num">{c.code}</span></div>
      </div>
    </div>
  </div>
);

export function Certificates() {
  const { data, loading } = useFetch("/me/certificates");
  if (loading) return <Loader />;
  return (
    <>
      <PageTitle title="Certificates" sub="Pass every quiz to unlock your certificate.">{data.length > 0 && <button className="btn-navy no-print" onClick={() => window.print()} data-testid="print-certificate-btn"><Printer /> Print / PDF</button>}</PageTitle>
      {data.length === 0 ? <Empty>No certificate yet. <Link to="/app/quizzes" className="underline">Keep going on your quizzes</Link>.</Empty> : <div className="space-y-6">{data.map((c) => <CertificateDoc key={c.id} c={c} />)}</div>}
    </>
  );
}

export function Materials() {
  const { data, loading } = useFetch("/me/materials");
  const [open, setOpen] = useState(null);
  if (loading) return <Loader />;
  return (
    <>
      <PageTitle title="Materials" sub={data.unlocked ? "Course binder chapters and slides." : "Materials unlock once you register for a class."} />
      <div className="grid gap-3 md:grid-cols-2">
        {data.items.map((m) => (
          <div key={m.id} className="panel p-4" data-testid={`material-${m.id}`}>
            <button className="flex w-full items-center gap-3 text-left" disabled={m.locked} onClick={() => setOpen(open === m.id ? null : m.id)}>
              {m.locked ? <Lock size={22} className="text-[#94A3B8]" /> : m.type === "slides" ? <Presentation size={22} className="text-[#E0A21B]" /> : <FileText size={22} className="text-[#E0A21B]" />}
              <span className="font-semibold">{m.title}</span>
            </button>
            {open === m.id && <div className="mt-3 whitespace-pre-line border-t pt-3 text-sm text-[#475569]">{m.body}{m.url && <a href={m.url} target="_blank" rel="noreferrer" className="mt-2 block font-semibold underline">Open file</a>}</div>}
          </div>
        ))}
      </div>
    </>
  );
}

export function Profile() {
  const { user, setUser } = useAuth();
  const { data: cities } = useFetch("/cities");
  const [f, setF] = useState({ name: user.name, phone: user.phone || "", city_slug: user.city_slug || "", marketing: !!user.email_prefs?.marketing });
  const [pw, setPw] = useState({ current: "", new: "" });
  const save = async () => { try { const { data } = await api.put("/me/profile", f); setUser(data); toast.success("Profile saved"); } catch (e) { toast.error(errMsg(e)); } };
  const changePw = async () => { try { await api.put("/me/password", pw); setPw({ current: "", new: "" }); toast.success("Password changed"); } catch (e) { toast.error(errMsg(e)); } };
  const exportData = async () => {
    const { data } = await api.get("/me/export");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([JSON.stringify(data, null, 2)], { type: "application/json" }));
    a.download = "mission-control-my-data.json";
    a.click();
  };
  const del = async () => { if (window.confirm("Send a request to delete your account and data?")) { await api.post("/me/delete-request"); toast.success("Deletion request sent to support"); } };
  return (
    <>
      <PageTitle title="Profile and settings" />
      <div className="grid gap-4 lg:grid-cols-2">
        <IndicatorCard />
        <div className="panel space-y-3 p-5">
          <h3 className="font-display text-xl font-bold uppercase">Your details</h3>
          <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Name" data-testid="profile-name-input" />
          <Input value={user.email} disabled />
          <Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} placeholder="Phone" data-testid="profile-phone-input" />
          <select value={f.city_slug} onChange={(e) => setF({ ...f, city_slug: e.target.value })} className="h-10 w-full rounded-md border px-3 text-sm" data-testid="profile-city-select">
            <option value="">Home city</option>{(cities || []).map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
          </select>
          <label className="flex items-center justify-between text-sm">Marketing emails <Switch checked={f.marketing} onCheckedChange={(v) => setF({ ...f, marketing: v })} data-testid="profile-marketing-switch" /></label>
          <button className="btn-gold" onClick={save} data-testid="profile-save-btn">Save</button>
        </div>
        <div className="space-y-4">
          <div className="panel space-y-3 p-5">
            <h3 className="font-display text-xl font-bold uppercase">Password</h3>
            <Input type="password" placeholder="Current password" value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} data-testid="pw-current-input" />
            <Input type="password" placeholder="New password (8+)" value={pw.new} onChange={(e) => setPw({ ...pw, new: e.target.value })} data-testid="pw-new-input" />
            <button className="btn-navy" onClick={changePw} data-testid="pw-change-btn">Change password</button>
          </div>
          <div className="panel space-y-3 p-5">
            <h3 className="font-display text-xl font-bold uppercase">Your data</h3>
            <p className="text-sm text-[#475569]">Download a copy of your data or ask us to delete it (PIPEDA / Law 25).</p>
            <div className="flex flex-wrap gap-2"><button className="btn-outline" onClick={exportData} data-testid="export-data-btn"><DownloadSimple /> Download my data</button><button className="btn-outline text-[#C8382E]" onClick={del} data-testid="delete-request-btn">Request deletion</button></div>
          </div>
        </div>
      </div>
    </>
  );
}

export function Notifications() {
  const { data, loading, reload } = useFetch("/me/notifications");
  if (loading) return <Loader />;
  return (
    <>
      <PageTitle title="Messages"><button className="btn-outline" onClick={async () => { await api.post("/me/notifications/read-all"); reload(); }} data-testid="mark-all-read-btn">Mark all read</button></PageTitle>
      {data.length === 0 ? <Empty>No messages yet.</Empty> : (
        <div className="space-y-2">
          {data.map((n) => (
            <div key={n.id} className={`panel p-4 ${n.read ? "" : "border-l-4 border-l-[#E0A21B]"}`} data-testid={`notification-${n.id}`}>
              <div className="flex justify-between gap-2"><span className="font-semibold">{n.title}</span><span className="text-xs text-[#475569]">{fmtDateTime(n.created_at)}</span></div>
              <p className="mt-1 text-sm text-[#475569]">{n.body}</p>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

export function Support() {
  const { data, loading, reload } = useFetch("/me/support");
  const [f, setF] = useState({ subject: "", message: "" });
  const [reply, setReply] = useState({});
  const create = async () => { try { await api.post("/me/support", f); setF({ subject: "", message: "" }); toast.success("Ticket created"); reload(); } catch (e) { toast.error(errMsg(e)); } };
  const send = async (id) => { try { await api.post(`/me/support/${id}/reply`, { message: reply[id] }); setReply({ ...reply, [id]: "" }); reload(); } catch (e) { toast.error(errMsg(e)); } };
  if (loading) return <Loader />;
  return (
    <>
      <PageTitle title="Support" />
      <div className="grid gap-4 lg:grid-cols-3">
        <div className="panel space-y-3 p-5 lg:order-2">
          <h3 className="font-display text-xl font-bold uppercase">New ticket</h3>
          <Input placeholder="Subject" value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} data-testid="support-subject-input" />
          <Textarea rows={4} placeholder="How can we help?" value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} data-testid="support-message-input" />
          <button className="btn-gold w-full" disabled={!f.subject || !f.message} onClick={create} data-testid="support-create-btn">Send</button>
        </div>
        <div className="space-y-3 lg:col-span-2">
          {data.length === 0 && <Empty>No tickets yet.</Empty>}
          {data.map((t) => (
            <div key={t.id} className="panel p-5" data-testid={`support-ticket-${t.number}`}>
              <div className="flex justify-between"><span className="font-semibold">#{t.number} · {t.subject}</span><span className="text-xs font-bold uppercase">{t.status}</span></div>
              <div className="mt-3 space-y-2">
                {t.messages.map((m, i) => (
                  <div key={i} className={`rounded-md p-3 text-sm ${m.from === "staff" ? "bg-[#0B1B33] text-white" : "bg-[#F4F6FA]"}`}>
                    <div className="text-xs opacity-70">{m.author} · {fmtDateTime(m.at)}</div><div className="mt-1 whitespace-pre-line">{m.body}</div>
                  </div>
                ))}
              </div>
              <div className="mt-3 flex gap-2"><Input placeholder="Reply…" value={reply[t.id] || ""} onChange={(e) => setReply({ ...reply, [t.id]: e.target.value })} data-testid={`support-reply-input-${t.number}`} /><button className="btn-navy" disabled={!reply[t.id]} onClick={() => send(t.id)} data-testid={`support-reply-btn-${t.number}`}>Reply</button></div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}
